import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";

import cls from "./AdminClubSingle.module.scss";

const STATUSES = ["pending", "open", "closed"];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
};

const IMAGE_SLOTS = [
  { key: "avatar", label: "Club Avatar (poster.webp)", presignPath: "avatar" },
  {
    key: "avatarThumb",
    label: "Avatar Thumb (480.webp)",
    presignPath: "avatar-thumb",
  },

  { key: "desktopOne", label: "Desktop One", presignPath: "desktop-one" },
  { key: "desktopTwo", label: "Desktop Two", presignPath: "desktop-two" },
  {
    key: "desktopHalfLeft",
    label: "Desktop Half Left",
    presignPath: "desktop-half-left",
  },
  {
    key: "desktopHalfRight",
    label: "Desktop Half Right",
    presignPath: "desktop-half-right",
  },

  { key: "mobileOne", label: "Mobile One", presignPath: "mobile-one" },
  { key: "mobileTwo", label: "Mobile Two", presignPath: "mobile-two" },
  {
    key: "mobileHalfLeft",
    label: "Mobile Half Left",
    presignPath: "mobile-half-left",
  },
  {
    key: "mobileHalfRight",
    label: "Mobile Half Right",
    presignPath: "mobile-half-right",
  },
];

const AdminClubSingle = () => {
  const { cityId, streetId, clubId } = useParams();
  const navigate = useNavigate();

  const [streetMeta, setStreetMeta] = useState(null);

  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");

  const [form, setForm] = useState({
    clubNo: "",
    clubName: "",
    status: "pending",
    clubVibe: "",
    clubIntro: "",
    clubDescription: "",
    sortOrder: 0,
  });

  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const [uploadErr, setUploadErr] = useState("");
  const [uploadMsg, setUploadMsg] = useState("");

  const [imageFiles, setImageFiles] = useState({});
  const [imagePreviews, setImagePreviews] = useState({});

  const hydrateForm = (doc) => {
    setForm({
      clubNo: typeof doc.clubNo === "number" ? String(doc.clubNo) : "",
      clubName: doc.clubName || "",
      status: doc.status || "pending",
      clubVibe: doc.clubVibe || "",
      clubIntro: doc.clubIntro || "",
      clubDescription: doc.clubDescription || "",
      sortOrder: typeof doc.sortOrder === "number" ? doc.sortOrder : 0,
    });
  };

  const fetchStreetMeta = useCallback(async () => {
    if (!cityId || !streetId) return;
    try {
      const { data } = await api.get(
        `/v1/admin/cities/${cityId}/streets/${streetId}`
      );
      setStreetMeta(data || null);
    } catch (e) {
      console.error("Failed to load street meta", e);
    }
  }, [cityId, streetId]);

  const fetchClub = useCallback(async () => {
    if (!cityId || !streetId || !clubId) return;
    try {
      setLoading(true);
      setErr("");
      const { data } = await api.get(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}`
      );

      setClub(data || null);
      if (data) hydrateForm(data);

      // keep chosen files, but reset previews if club changes a lot
      // (you can remove these two lines if you prefer keeping previews)
      // setImageFiles({});
      // setImagePreviews({});
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.message || "Could not load club.";
      setErr(msg);
      setClub(null);
    } finally {
      setLoading(false);
    }
  }, [cityId, streetId, clubId]);

  useEffect(() => {
    fetchStreetMeta();
    fetchClub();
  }, [fetchStreetMeta, fetchClub]);

  const streetName = streetMeta?.streetName || "Street";

  const backToClubsPath = useMemo(() => {
    if (!cityId || !streetId) return "/admin/cities";
    return `/admin/cities/single/${cityId}/streets/${streetId}/clubs`;
  }, [cityId, streetId]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSave = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");
    setUploadErr("");
    setUploadMsg("");

    if (!form.clubName.trim()) {
      setErr("Club name is required.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        clubName: form.clubName.trim(),
        status: form.status,
        clubVibe: form.clubVibe,
        clubIntro: form.clubIntro,
        clubDescription: form.clubDescription,
        sortOrder: Number(form.sortOrder) || 0,
        clubNo:
          form.clubNo === ""
            ? null
            : Number.isFinite(Number(form.clubNo))
            ? Number(form.clubNo)
            : null,
      };

      const { data } = await api.patch(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}`,
        payload
      );

      setClub(data || null);
      if (data) hydrateForm(data);

      setSuccess("Saved.");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to save.";
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (slotKey, file) => {
    setUploadErr("");
    setUploadMsg("");

    setImageFiles((p) => ({ ...p, [slotKey]: file || null }));

    if (!file) {
      setImagePreviews((p) => {
        const copy = { ...p };
        delete copy[slotKey];
        return copy;
      });
      return;
    }

    const url = URL.createObjectURL(file);
    setImagePreviews((p) => ({ ...p, [slotKey]: url }));
  };

  const uploadSlot = async (slotKey, presignPath) => {
    const file = imageFiles[slotKey];
    if (!file) {
      setUploadErr("Select a file first.");
      return;
    }

    try {
      setUploadingKey(slotKey);
      setUploadErr("");
      setUploadMsg("");
      setSuccess("");
      setErr("");

      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const contentType = file.type || "application/octet-stream";

      // 1) get presigned PUT url
      const { data } = await api.post(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}/presign/${presignPath}`,
        { ext, contentType }
      );

      const uploadUrl = data?.uploadUrl;
      if (!uploadUrl) throw new Error("No uploadUrl returned.");

      // 2) PUT file to S3
      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });

      if (!res.ok) throw new Error("Upload failed.");

      setUploadMsg(`${slotKey} uploaded.`);
      await fetchClub();
    } catch (e) {
      const msg = e?.message || "Upload failed.";
      setUploadErr(msg);
    } finally {
      setUploadingKey("");
    }
  };

  const mainAvatarUrl = useMemo(() => {
    return (
      imagePreviews.avatar ||
      club?.avatar?.url ||
      club?.avatarThumb?.url ||
      null
    );
  }, [imagePreviews.avatar, club]);

  if (loading && !club) {
    return <div className={cls.page}>Loading…</div>;
  }

  if (!club) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button className={cls.backBtn} onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1 className={cls.title}>Club</h1>
          <p className={cls.subtitle}>Not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.header}>
        <Link className={cls.backLink} to={backToClubsPath}>
          ← {streetName} · Clubs
        </Link>

        <h1 className={cls.title}>Club Settings</h1>
        <p className={cls.subtitle}>
          Edit details and upload images for this club.
        </p>
      </div>
      {/* Admin club quick links */}
      <nav className={cls.quickLinks}>
        <Link
          to={`/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/rules`}
          className={cls.quickLink}
        >
          📜 Rules
        </Link>

        <Link
          to={`/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/roles`}
          className={cls.quickLink}
        >
          🧩 Roles
        </Link>

        <Link
          to={`/admin/clubs/single/${clubId}/membership-requests`}
          className={cls.quickLink}
        >
          ✋ Requests
        </Link>

        <Link
          to={`/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/members`}
          className={cls.quickLink}
        >
          👥 Members
        </Link>
        <Link
          to={`/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/activities`}
          className={cls.quickLink}
        >
          🎮 Activities
        </Link>
      </nav>

      <div className={cls.metaBar}>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Club</span>
          <span className={cls.metaValue}>
            {club.clubName || "Untitled"}{" "}
            {club.clubNo ? `· #${club.clubNo}` : ""}
          </span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Status</span>
          <span className={cls.metaValue}>{club.status || "—"}</span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Updated</span>
          <span className={cls.metaValue}>{formatDate(club.updatedAt)}</span>
        </div>
      </div>

      {err && <div className={cls.error}>⚠ {err}</div>}
      {success && <div className={cls.success}>{success}</div>}
      {uploadErr && <div className={cls.error}>⚠ {uploadErr}</div>}
      {uploadMsg && <div className={cls.success}>{uploadMsg}</div>}

      <div className={cls.layout}>
        {/* LEFT: Images */}
        <section className={cls.avatarCard}>
          <div className={cls.avatarHeader}>
            <h2 className={cls.cardTitle}>Images</h2>
            <p className={cls.cardSubtitle}>
              Choose a file, preview it, then upload.
            </p>
          </div>

          <div className={cls.avatarPreviewWrap}>
            <div className={cls.avatarGlow}>
              {mainAvatarUrl ? (
                <img
                  src={mainAvatarUrl}
                  alt="Club avatar"
                  className={cls.avatarImage}
                />
              ) : (
                <div className={cls.avatarFallback}>No avatar</div>
              )}
            </div>
            <p className={cls.avatarMeta}>Avatar shown in lists and headers.</p>
          </div>

          <div className={cls.imagesGrid}>
            {IMAGE_SLOTS.map((slot) => {
              const picked = imagePreviews[slot.key];
              const serverUrl = club?.[slot.key]?.url || null;
              const previewUrl = picked || serverUrl || null;
              const file = imageFiles[slot.key];

              return (
                <div key={slot.key} className={cls.imageSlot}>
                  <p className={cls.imageLabel}>{slot.label}</p>

                  <div className={cls.imagePreviewMini}>
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={slot.label}
                        className={cls.miniImage}
                      />
                    ) : (
                      <div className={cls.miniFallback}>No image</div>
                    )}
                  </div>

                  <label className={cls.fileLabel}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleFileChange(slot.key, e.target.files?.[0] || null)
                      }
                    />
                    <span className={cls.fileLabelText}>
                      {file ? "Change file" : "Choose file"}
                    </span>
                  </label>

                  {file?.name && (
                    <div className={cls.fileName}>{file.name}</div>
                  )}

                  <button
                    type="button"
                    className={cls.avatarBtn}
                    onClick={() => uploadSlot(slot.key, slot.presignPath)}
                    disabled={uploadingKey === slot.key || !file}
                  >
                    {uploadingKey === slot.key ? "Uploading…" : "Upload"}
                  </button>

                  <div className={cls.keysList}>
                    <strong>Key</strong>
                    <code>{club?.[slot.key]?.key || "—"}</code>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* RIGHT: Form */}
        <section className={cls.formCard}>
          <div className={cls.formHeader}>
            <h2 className={cls.cardTitle}>Details</h2>
            <p className={cls.cardSubtitle}>Update club fields.</p>
          </div>

          <form className={cls.form} onSubmit={onSave}>
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label}>Club No</label>
                <input
                  type="number"
                  className={cls.input}
                  value={form.clubNo}
                  onChange={(e) => setField("clubNo", e.target.value)}
                  placeholder="101"
                />
                <p className={cls.help}>Optional.</p>
              </div>

              <div className={cls.field}>
                <label className={cls.label}>Status</label>
                <select
                  className={cls.select}
                  value={form.status}
                  onChange={(e) => setField("status", e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={cls.field}>
              <label className={cls.label}>Club Name</label>
              <input
                className={cls.input}
                value={form.clubName}
                onChange={(e) => setField("clubName", e.target.value)}
                placeholder="Velvet Lounge"
                required
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label}>Sort Order</label>
              <input
                type="number"
                className={cls.input}
                value={form.sortOrder}
                onChange={(e) => setField("sortOrder", e.target.value)}
              />
              <p className={cls.help}>Lower shows first.</p>
            </div>

            <div className={cls.field}>
              <label className={cls.label}>Vibe</label>
              <input
                className={cls.input}
                value={form.clubVibe}
                onChange={(e) => setField("clubVibe", e.target.value)}
                placeholder="Intimate, bold, magnetic…"
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label}>Intro</label>
              <textarea
                rows={3}
                className={cls.input}
                value={form.clubIntro}
                onChange={(e) => setField("clubIntro", e.target.value)}
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label}>Description</label>
              <textarea
                rows={4}
                className={cls.input}
                value={form.clubDescription}
                onChange={(e) => setField("clubDescription", e.target.value)}
              />
            </div>

            <div className={cls.actions}>
              <button
                className={cls.primaryBtn}
                type="submit"
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default AdminClubSingle;
